import { extractTextFromDocx, extractTextFromPdf } from "./canvasParser";
import { extractPerspectiveSourceUrl } from "./lessonPlanApi";

const TEXT_EXTENSIONS = new Set([
  "txt",
  "md",
  "csv",
  "json",
  "html",
  "htm",
  "xml",
]);
const DOCX_EXTENSIONS = new Set(["docx"]);
const PDF_EXTENSIONS = new Set(["pdf"]);
const GOOGLE_DOC_HOST_PATTERN = /(^|\.)docs\.google\.com$/i;
const GOOGLE_DOC_PATH_PATTERN = /\/document\/(?:u\/\d+\/)?d\/([a-zA-Z0-9-_]+)/i;
const BLOCKED_AUTOMATION_ERROR_PATTERN = /blocked automated access/i;
const RATE_LIMITED_ERROR_PATTERN = /rate-limiting requests/i;
const GENERIC_FETCH_ERROR_PATTERN = /unable to fetch that url right now/i;
const NO_READABLE_TEXT_ERROR_PATTERN =
  /we could not extract readable text from (?:that|this) url/i;
const UNSUPPORTED_URL_ERROR_PATTERN =
  /only webpage, text, pdf, and docx urls are supported right now/i;
const PLACEHOLDER_URL_TITLES = new Set(["edit", "export", "preview", "view"]);

const normalizeExtractedText = (value = "") =>
  String(value || "")
    .replace(/\r/g, "")
    .replace(/\s+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const truncateText = (value = "", maxChars = 6000) => {
  const normalized = normalizeExtractedText(value);
  if (!normalized || normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trim()}...`;
};

const getFileExtension = (file) =>
  String(file?.name || "")
    .split(".")
    .pop()
    ?.toLowerCase() || "";

const getUrlHostnameLabel = (value = "") => {
  try {
    return new URL(String(value || "")).hostname.replace(/^www\./i, "");
  } catch (error) {
    return String(value || "").trim();
  }
};

const buildUrlDocumentType = (kind = "", contentType = "") => {
  if (kind === "pdf") return "PDF URL";
  if (kind === "docx") return "DOCX URL";
  if (kind === "html") return "Web page";
  if (kind === "text") return "Text URL";
  return contentType || "URL";
};

const extractGoogleDocId = (value = "") => {
  try {
    const parsed = new URL(String(value || ""));
    if (!GOOGLE_DOC_HOST_PATTERN.test(parsed.hostname)) {
      return "";
    }
    const match = parsed.pathname.match(GOOGLE_DOC_PATH_PATTERN);
    return match?.[1] || "";
  } catch (error) {
    return "";
  }
};

const buildReadableDocumentUrl = (normalizedUrl = "") => {
  const googleDocId = extractGoogleDocId(normalizedUrl);
  if (!googleDocId) {
    return normalizedUrl;
  }
  return `https://docs.google.com/document/d/${googleDocId}/export?format=txt`;
};

const buildUrlDocumentName = ({
  title = "",
  fallbackName = "",
  originalUrl = "",
} = {}) => {
  const normalizedTitle = String(title || "").trim();
  const googleDocId = extractGoogleDocId(originalUrl);
  if (
    normalizedTitle &&
    !PLACEHOLDER_URL_TITLES.has(normalizedTitle.toLowerCase())
  ) {
    return normalizedTitle;
  }
  if (googleDocId) {
    return "Google Doc";
  }
  return fallbackName || normalizedTitle || "URL";
};

const buildNormalizedUrlExtractionError = (message = "") => {
  const normalizedMessage = String(message || "").trim();

  if (BLOCKED_AUTOMATION_ERROR_PATTERN.test(normalizedMessage)) {
    return "This site blocks automated readers. Pages behind login, paywalls, bot protection, or JavaScript-heavy rendering usually cannot be imported automatically. Try a public PDF, DOCX, or text URL, or paste the text directly.";
  }

  if (RATE_LIMITED_ERROR_PATTERN.test(normalizedMessage)) {
    return "This site is rate-limiting requests right now. Try again later, use a public PDF, DOCX, or text URL, or paste the text directly.";
  }

  if (UNSUPPORTED_URL_ERROR_PATTERN.test(normalizedMessage)) {
    return "This URL is not a supported readable source. Use a web page, PDF, DOCX, or plain-text link.";
  }

  if (
    GENERIC_FETCH_ERROR_PATTERN.test(normalizedMessage) ||
    NO_READABLE_TEXT_ERROR_PATTERN.test(normalizedMessage)
  ) {
    return "We could not extract readable text from this URL. Some pages only load correctly in a browser. Try a public PDF, DOCX, or text URL, or paste the text directly.";
  }

  return (
    normalizedMessage ||
    "We could not extract readable text from this URL. Try a public PDF, DOCX, or text URL, or paste the text directly."
  );
};

export const normalizeDocumentUrl = (value = "") => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const withProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const parsed = new URL(withProtocol);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return "";
    }
    parsed.hash = "";
    return parsed.toString();
  } catch (error) {
    return "";
  }
};

export const parseDocumentUrlInput = (value = "") =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\r\n\s]+/)
        .map((item) => normalizeDocumentUrl(item))
        .filter(Boolean)
    )
  );

const stripHtmlToText = (value = "") => {
  if (!value) return "";
  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(String(value), "text/html");
    return normalizeExtractedText(doc.body?.textContent || "");
  }
  return normalizeExtractedText(String(value).replace(/<[^>]+>/g, " "));
};

const isTextFile = (file) => {
  if (file?.type && String(file.type).startsWith("text/")) return true;
  return TEXT_EXTENSIONS.has(getFileExtension(file));
};

const extractContentFromFile = async (file) => {
  const extension = getFileExtension(file);

  if (isTextFile(file)) {
    const text = await file.text();
    if (extension === "html" || extension === "htm") {
      return stripHtmlToText(text);
    }
    return normalizeExtractedText(text);
  }

  if (DOCX_EXTENSIONS.has(extension)) {
    return normalizeExtractedText(await extractTextFromDocx(file));
  }

  if (PDF_EXTENSIONS.has(extension)) {
    return normalizeExtractedText(await extractTextFromPdf(file));
  }

  return "";
};

export const readDocumentFiles = async (files, { maxChars = 6000 } = {}) => {
  const entries = [];

  for (const file of Array.isArray(files) ? files : []) {
    let content = "";
    let extractionError = "";

    try {
      content = truncateText(await extractContentFromFile(file), maxChars);
    } catch (error) {
      console.error("Unable to extract file text:", file?.name, error);
      extractionError =
        error?.message || "Unable to extract text from this file.";
    }

    entries.push({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      type: file.type || "file",
      size: file.size,
      content,
      extractionError,
    });
  }

  return entries;
};

export const readDocumentUrls = async (urls, { maxChars = 6000 } = {}) => {
  const entries = [];

  for (const rawUrl of Array.isArray(urls) ? urls : []) {
    const normalizedUrl = normalizeDocumentUrl(rawUrl);
    const fallbackName = getUrlHostnameLabel(normalizedUrl || rawUrl);

    if (!normalizedUrl) {
      entries.push({
        id: `url-${String(rawUrl || "").trim() || "invalid"}`,
        name: fallbackName || "URL",
        type: "URL",
        size: 0,
        content: "",
        extractionError: "Enter a valid http(s) URL.",
        sourceUrl: "",
      });
      continue;
    }

    try {
      const readableUrl = buildReadableDocumentUrl(normalizedUrl);
      const result = await extractPerspectiveSourceUrl({ url: readableUrl });
      const finalUrl = normalizeDocumentUrl(result?.url || readableUrl) || readableUrl;
      const content = truncateText(String(result?.content || ""), maxChars);
      const googleDocId = extractGoogleDocId(normalizedUrl);

      entries.push({
        id: `url-${normalizedUrl}`,
        name: buildUrlDocumentName({
          title: result?.title,
          fallbackName,
          originalUrl: normalizedUrl,
        }),
        type: googleDocId
          ? "Google Doc"
          : buildUrlDocumentType(result?.kind, result?.contentType),
        size: Number(result?.size) || content.length,
        content,
        extractionError: content
          ? ""
          : "We could not extract readable text from this URL. Try a public PDF, DOCX, or text URL, or paste the text directly.",
        sourceUrl: googleDocId ? normalizedUrl : finalUrl,
        contentType: String(result?.contentType || "").trim(),
      });
    } catch (error) {
      entries.push({
        id: `url-${normalizedUrl}`,
        name: fallbackName || normalizedUrl,
        type: "URL",
        size: 0,
        content: "",
        extractionError: buildNormalizedUrlExtractionError(error?.message),
        sourceUrl: normalizedUrl,
      });
    }
  }

  return entries;
};

export const buildCombinedDocumentText = ({ text = "", documents = [] } = {}) => {
  const blocks = [];
  const normalizedText = normalizeExtractedText(text);

  if (normalizedText) {
    blocks.push(`Primary text:\n${normalizedText}`);
  }

  (Array.isArray(documents) ? documents : [])
    .filter((document) => normalizeExtractedText(document?.content))
    .forEach((document, index) => {
      blocks.push(
        `Supporting document ${index + 1}: ${document.name || "Untitled document"}\n${normalizeExtractedText(
          document.content
        )}`
      );
    });

  return blocks.join("\n\n").trim();
};

export const hasUsableDocumentText = (documents = []) =>
  (Array.isArray(documents) ? documents : []).some((document) =>
    Boolean(normalizeExtractedText(document?.content))
  );
